import "./session-replay";

import { SessionReplay } from "./session-replay";
import { Storybook } from "@tento-ui/ui/storybook";

// The transcripts below are REAL OUTPUT, captured through a pty and kept
// verbatim — with one clearly-marked exception at the bottom.
//
// That first sentence was already here and was already FALSE, which is the
// more important half of this file's history. Two of the three samples were
// composed: one padded `tsh lona ts` into aligned columns, which tsh does not
// do — it separates a command from its description with exactly two spaces and
// lets them run ragged — and one had `tsh lona ts check` print
// "checking 1284 files / ok", a sentence the binary has never printed (it
// prints a per-domain status table). The third claimed bare `tsh` lists its
// commands; what actually prints that list is an UNRECOGNISED command, which
// is the better demonstration anyway.
//
// A storybook is where a component's claims are checked, so a fabricated
// sample here is worse than one on a marketing page: it is what a reviewer
// trusts instead of running the command.

const LS = `$ tsh ls tento/tento-sh

tento/tento-sh — 10 entries
NAME         KIND SIZE MODIFIED
───────────────────────────────
.vscode/     dir     -     100d
docs/        dir     -      68d
examples/    dir     -      90d
src/         dir     -      33d
tests/       dir     -      33d
.gitignore   file  15B     100d
Cargo.lock   file  34K     100d
Cargo.toml   file 872B      33d
README.md    file 1.6K      71d
rustfmt.toml file  14B     100d`;

const GROUP = `$ tsh lona ts

  check  Checks typescript code
  routes.check  Check that routes folders map to servable routes
  tests  Runs tests on any *.deno.test.ts code
  vscode.enable-paths  Updates VS Code deno.enablePaths`;

const INFO = `$ tsh table.info

ls-1787171736054 — available columns
KEY         HEADER   FORMAT     VISIBLE
───────────────────────────────────────
name        NAME     text       yes
kind        KIND     text       yes
size_bytes  SIZE     size_bytes yes
modified_ms MODIFIED age_ms     yes
perms       PERMS    text       -
owner       OWNER    text       -
group       GROUP    text       -
nlink       NLINK    text       -`;

const DISCOVER = `$ tsh nosuchcmd

error: no command \`nosuchcmd\`
  available commands
    cat
    cd
    claude
    cp
    echo
    head
    lona
    lona-admin
    lona-js
    ls
    mkdir
    mv
    pwd
    rm
    scene.list
    session
    session.list
    table.columns
    table.hide
    table.info
    table.reset
    table.show
    table.toggle
    tail
    version`;

// The one synthetic sample, and synthetic on purpose.
//
// tsh's piped output is monochrome: a whole `ls` capture contains a single
// escape pair, on a startup warning. Its colour lives in the interactive TUI,
// which cannot be driven headlessly on the host this was written on. Rather
// than fake a tsh session in colour, this exercises the DECODER against the
// exact vocabulary `tento-term/src/ansi.rs` emits — the eight base colours,
// the 256-index grey it rules with, bold, dim and reset.
const SGR = [
  "$ demo",
  "",
  "\u001b[1mSTATUS\u001b[0m  \u001b[38;5;238m────────────\u001b[0m",
  "\u001b[32m●\u001b[0m ok    build",
  "\u001b[33m●\u001b[0m warn  lint",
  "\u001b[31m●\u001b[0m fail  tests",
  "\u001b[2ma dimmed trailing note\u001b[0m",
  "\u001b[36mcyan\u001b[0m and \u001b[35mmagenta\u001b[0m and \u001b[1;34mbold blue\u001b[0m",
].join("\n");

function $framed(vm: SessionReplay.ViewModel): HTMLElement {
  const $replay = SessionReplay.makeWith(vm);
  $replay.assignStyles({
    fontFamily: "ui-monospace, monospace",
    fontSize: "13px",
    lineHeight: "1.6",
    padding: "16px",
    background: "var(--bg-elevated)",
    color: "var(--text-sec)",
  });
  return Storybook.fill($replay);
}

export class SessionReplayStoryBook extends Storybook.Default {
  title(): string {
    return "Session Replay";
  }

  tags(): string[] {
    return ["std-session-replay"];
  }

  examples(): Storybook.Example[] {
    return [
      {
        // The default row, and the component declaring no colour of its own:
        // everything below is inherited from the frame around it.
        title: "Default",
        render: () => $framed({ sample: LS, $line: null, timing: null, prompt: null }),
      },
      {
        // Alignment is content: two spaces between a command and its
        // description, descriptions ragged. `white-space: pre` is what keeps
        // that intact.
        title: "Two-space columns",
        render: () => $framed({ sample: GROUP, $line: null, timing: null, prompt: null }),
      },
      {
        // Blank lines have to hold their row, or the transcript closes up
        // wherever its author left a gap. This one has a real blank after the
        // command, which is what the binary prints.
        title: "Blank lines keep their rows",
        render: () => $framed({ sample: INFO, $line: null, timing: null, prompt: null }),
      },
      {
        // The longest sample here, and the one that exercises the row cap: it
        // overflows, so it scrolls inside itself and earns a tab stop.
        title: "Overflowing transcript",
        render: () => $framed({ sample: DISCOVER, $line: null, timing: null, prompt: null }),
      },
      {
        // The prompt pinned to the bottom — the shape of the TUI. The command
        // is typed down here and joins the scrollback above on submit, and the
        // caret stays at the prompt while output arrives, because that is
        // where a cursor waits in a shell.
        title: "Prompt pinned to the bottom",
        render: () => $framed({ sample: LS, $line: null, timing: null, prompt: "$" }),
      },
      {
        // A lane carries its own prompt label, so a replay of one should look
        // like that lane rather than like sh.
        title: "A lane prompt",
        render: () => $framed({ sample: GROUP, $line: null, timing: null, prompt: "oc>" }),
      },
      {
        // Colour, decoded from SGR rather than styled by the caller.
        title: "ANSI colour",
        render: () => $framed({ sample: SGR, $line: null, timing: null, prompt: "$" }),
      },
      {
        // A caller bringing its own row element — the seam that lets one engine
        // drive four different-looking terminals. It still gets the colour:
        // `paintInto` is what `$line` would otherwise re-implement.
        title: "Caller-supplied row",
        render: () =>
          $framed({
            sample: SGR,
            timing: null,
            prompt: null,
            $line: (line) => {
              const $row = document.createElement("div");
              SessionReplay.paintInto($row, line);
              if (line.mode === "typed") $row.style.color = "var(--text-pri)";
              if (line.mode === "blank") $row.style.minHeight = "1em";
              return $row;
            },
          }),
      },
      {
        // Instant, for a figure that should be readable the moment it is
        // reached rather than typed out.
        title: "No typing delay",
        render: () =>
          $framed({
            sample: LS,
            $line: null,
            timing: { char: 0, work: 0, blank: 0 },
            prompt: null,
          }),
      },
      {
        // The degenerate input: nothing to replay, and nothing thrown.
        title: "Empty transcript",
        render: () => $framed({ sample: "", $line: null, timing: null, prompt: null }),
      },
    ];
  }
}
